import React, { useState, useEffect } from 'react';
import SignaturePad from './SignaturePad';

const HRAdminConsole = () => {
  const [activeTab, setActiveTab] = useState('create');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Create Employee Form
  const [newEmployee, setNewEmployee] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'employee'
  });

  const [offerLetter, setOfferLetter] = useState(null);
  const [offerLetterPreview, setOfferLetterPreview] = useState(null);
  const [emailConflict, setEmailConflict] = useState(null); // 409 from create: who already has this email
  const [conflictBusy, setConflictBusy] = useState(false);

  useEffect(() => {
    fetchPendingEmployees();
  }, []);

  const fetchPendingEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/hr/pending-onboarding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const [deletingId, setDeletingId] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [reviewingEmployee, setReviewingEmployee] = useState(null);
  const [reviewData, setReviewData] = useState(null);

  const handleDeleteEmployee = async (employeeId, employeeName) => {
    // Set confirmation state instead of using window.confirm
    setConfirmDelete({ id: employeeId, name: employeeName });
  };

  const confirmDeleteEmployee = async () => {
    const { id: employeeId, name: employeeName } = confirmDelete;
    setDeletingId(employeeId);
    setConfirmDelete(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/hr/delete-employee/${employeeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Remove from local state immediately for instant feedback
        setEmployees(prevEmployees => prevEmployees.filter(emp => emp.id !== employeeId));
        // Show success message
        setTimeout(() => {
          alert('✅ Employee deleted successfully');
        }, 100);
      } else {
        const error = await response.json();
        alert(`❌ Failed to delete employee: ${error.detail}`);
        // Refresh list to show current state
        await fetchPendingEmployees();
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
      alert('❌ Error deleting employee. Please try again.');
      await fetchPendingEmployees();
    } finally {
      setDeletingId(null);
    }
  };

  const handleApproveEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Approve ${employeeName}'s onboarding?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/hr/approve-onboarding/${employeeId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert(`✅ ${employeeName} approved! They can now access the system.`);
        await fetchPendingEmployees();
      } else {
        const error = await response.json();
        alert(`❌ Failed to approve: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error approving employee:', error);
      alert('❌ Error approving employee. Please try again.');
    }
  };

  const handleRejectEmployee = async (employeeId, employeeName) => {
    const reason = window.prompt(`Reject ${employeeName}'s onboarding?\n\nPlease provide a reason:`);
    if (!reason) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/hr/reject-onboarding/${employeeId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        alert(`⚠️ ${employeeName}'s onboarding rejected. They will need to resubmit.`);
        await fetchPendingEmployees();
        setReviewingEmployee(null);
        setReviewData(null);
      } else {
        const error = await response.json();
        alert(`❌ Failed to reject: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error rejecting employee:', error);
      alert('❌ Error rejecting employee. Please try again.');
    }
  };

  const [overrideStep, setOverrideStep] = useState('');
  const [overrideSaving, setOverrideSaving] = useState(false);

  const applyOverride = async (payload) => {
    if (!reviewData?.id) return;
    setOverrideSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/hr/onboarding-override/${reviewData.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok) {
        alert(`✅ Override applied: ${(data.changes || []).join(', ')}`);
        setOverrideStep('');
        await handleReviewEmployee(reviewData.id);
        await fetchPendingEmployees();
      } else {
        alert(`❌ Override failed: ${data.detail}`);
      }
    } catch (error) {
      console.error('Error applying override:', error);
      alert('❌ Error applying override. Please try again.');
    } finally {
      setOverrideSaving(false);
    }
  };

  const handleReviewEmployee = async (employeeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/hr/employee-onboarding-details/${employeeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReviewData(data);
        setReviewingEmployee(employeeId);
      } else {
        alert('Failed to load employee details');
      }
    } catch (error) {
      console.error('Error loading employee details:', error);
      alert('Error loading employee details');
    }
  };

  // Shared renderers so the desktop table and the mobile cards stay in sync
  const statusBadge = (emp) => {
    if (emp.onboarding_status === 'pending_review' || emp.status === 'Completed - Pending Review') {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">✓ Complete - Ready for Review</span>;
    }
    if (emp.onboarding_status === 'approved') {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">✓ Approved</span>;
    }
    if (emp.onboarding_status === 'rejected') {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">✗ Rejected</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{emp.status || `Step ${Math.min(emp.currentStep || emp.current_step || 1, 8)}/8`}</span>;
  };

  const actionButtons = (emp) => {
    const readyForReview = emp.onboarding_status === 'pending_review' || emp.status === 'Completed - Pending Review';
    return (
      <>
        <button
          onClick={() => handleReviewEmployee(emp.id)}
          className="px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
          data-testid={`hr-review-${emp.id}`}
        >
          📋 Review
        </button>
        {readyForReview && (
          <>
            <button
              onClick={() => handleApproveEmployee(emp.id, emp.name)}
              className="px-3 py-1.5 text-sm text-white bg-green-600 hover:bg-green-700 rounded transition-colors"
              data-testid={`hr-approve-${emp.id}`}
            >
              ✓ Approve
            </button>
            <button
              onClick={() => handleRejectEmployee(emp.id, emp.name)}
              className="px-3 py-1.5 text-sm text-white bg-yellow-600 hover:bg-yellow-700 rounded transition-colors"
              data-testid={`hr-reject-${emp.id}`}
            >
              ✗ Reject
            </button>
          </>
        )}
        <button
          onClick={() => handleDeleteEmployee(emp.id, emp.name)}
          disabled={deletingId === emp.id}
          className="px-3 py-1.5 text-sm text-white bg-red-600 hover:bg-red-700 rounded disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          data-testid={`hr-delete-${emp.id}`}
        >
          {deletingId === emp.id ? 'Deleting...' : '🗑 Delete'}
        </button>
      </>
    );
  };

  const submitCreate = async (token) => {
    const formData = new FormData();
    formData.append('firstName', newEmployee.firstName.trim());
    formData.append('lastName', newEmployee.lastName.trim());
    formData.append('email', newEmployee.email.trim().toLowerCase());
    formData.append('password', newEmployee.password);
    formData.append('role', newEmployee.role);
    formData.append('offerLetter', offerLetter);
    return fetch(`${backendUrl}/api/hr/create-employee`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
  };

  const afterCreated = async (result) => {
    alert(`✅ Employee Created Successfully!\n\nEmail: ${result.email}\n\nThe employee can now log in and complete their onboarding.`);
    setNewEmployee({ firstName: '', lastName: '', email: '', password: '', role: 'employee' });
    setOfferLetter(null);
    setOfferLetterPreview(null);
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
    await fetchPendingEmployees();
    setActiveTab('pending');
  };

  // "That email is already on X's account" → delete the stray account, then create again
  const deleteConflictAndRetry = async () => {
    const ex = emailConflict?.existing;
    if (!ex) return;
    setConflictBusy(true);
    try {
      const token = localStorage.getItem('token');
      const del = await fetch(`${backendUrl}/api/users/${ex.id}/permanent`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (!del.ok) {
        const err = await del.json().catch(() => ({}));
        alert(`Could not delete ${ex.full_name}: ${err.detail || 'unknown error'}`);
        return;
      }
      const response = await submitCreate(token);
      if (response.ok) {
        setEmailConflict(null);
        await afterCreated(await response.json());
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Deleted ${ex.full_name}'s old account, but creating again failed: ${typeof err.detail === 'string' ? err.detail : err.detail?.message || 'Unknown error'}`);
      }
    } catch (e) {
      alert('Network problem — please try again.');
    } finally {
      setConflictBusy(false);
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    if (!newEmployee.firstName || !newEmployee.lastName || !newEmployee.email || !newEmployee.password) {
      alert('Please fill in all required fields');
      return;
    }

    if (!offerLetter) {
      alert('Please upload an offer letter PDF');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmployee.email)) {
      alert('Please enter a valid email address');
      return;
    }

    // Password length check
    if (newEmployee.password.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('Authentication error. Please log in again.');
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('firstName', newEmployee.firstName.trim());
      formData.append('lastName', newEmployee.lastName.trim());
      formData.append('email', newEmployee.email.trim().toLowerCase());
      formData.append('password', newEmployee.password);
      formData.append('role', newEmployee.role);
      formData.append('offerLetter', offerLetter);

      const response = await submitCreate(token);

      if (response.ok) {
        await afterCreated(await response.json());
      } else {
        const error = await response.json();
        if (response.status === 409 && error.detail?.code === 'email_taken') {
          setEmailConflict(error.detail);
          return;
        }
        alert(`Failed to create employee: ${typeof error.detail === 'string' ? error.detail : error.detail?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating employee:', error);
      alert('Failed to create employee. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setOfferLetter(file);
      setOfferLetterPreview(URL.createObjectURL(file));
    } else {
      alert('Please select a PDF file');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {emailConflict && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="email-conflict-dialog">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900">This email is already in Ops</h3>
            <p className="text-sm text-gray-700 mt-2">{emailConflict.message}</p>
            <div className="mt-3 rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm text-gray-800" data-testid="email-conflict-existing">
              <p className="font-semibold">{emailConflict.existing?.full_name}</p>
              <p className="text-xs text-gray-600">
                {emailConflict.existing?.role || 'employee'} · {emailConflict.existing?.is_active ? 'active' : 'removed / inactive'} ·{' '}
                {emailConflict.existing?.is_onboarded ? 'onboarded' : 'never finished onboarding'} ·{' '}
                {emailConflict.existing?.work_records ? `${emailConflict.existing.work_records} hours/clock records` : 'no hours recorded'}
              </p>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button type="button" onClick={() => setEmailConflict(null)} disabled={conflictBusy}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium" data-testid="email-conflict-cancel">
                Use a different email
              </button>
              {emailConflict.existing?.can_delete ? (
                <button type="button" onClick={deleteConflictAndRetry} disabled={conflictBusy}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-60" data-testid="email-conflict-delete-retry">
                  {conflictBusy ? 'Deleting & creating…' : `Delete ${emailConflict.existing.full_name.split(' ')[0]}'s old account & create new`}
                </button>
              ) : (
                <p className="text-xs text-gray-500 self-center">Can't delete — this account has hours on it. Reactivate it in Team, or use another email.</p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">HR Admin Console</h1>
          <p className="text-gray-600">Manage employee onboarding</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('create')}
                className={`py-4 px-6 font-medium border-b-2 ${
                  activeTab === 'create'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Create New Employee
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`py-4 px-6 font-medium border-b-2 ${
                  activeTab === 'pending'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Pending Onboarding ({employees.length})
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'create' && (
              <form onSubmit={handleCreateEmployee} className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Create Employee Account & Upload Offer Letter</strong><br/>
                    The employee will receive their login credentials and can sign their offer letter before starting onboarding.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      required
                      value={newEmployee.firstName}
                      onChange={(e) => setNewEmployee({...newEmployee, firstName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      required
                      value={newEmployee.lastName}
                      onChange={(e) => setNewEmployee({...newEmployee, lastName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={newEmployee.email}
                      onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Temporary Password *</label>
                    <input
                      type="text"
                      required
                      value={newEmployee.password}
                      onChange={(e) => setNewEmployee({...newEmployee, password: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Employee will use this to login"
                    />
                  </div>
                </div>

                <div className="border-t pt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Upload Offer Letter (PDF) *</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {offerLetterPreview && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                      <p className="text-sm text-green-800">✓ Offer letter selected: {offerLetter.name}</p>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:bg-gray-400 transition-colors"
                >
                  {loading ? 'Creating...' : 'Create Employee Account'}
                </button>
              </form>
            )}

            {activeTab === 'pending' && (
              <div className="space-y-4">
                {employees.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>No pending onboarding employees</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop / tablet: table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {employees.map((emp) => (
                            <tr key={emp.id}>
                              <td className="px-6 py-4 whitespace-nowrap">{emp.name}</td>
                              <td className="px-6 py-4 whitespace-nowrap">{emp.email}</td>
                              <td className="px-6 py-4 whitespace-nowrap">{statusBadge(emp)}</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex gap-2">{actionButtons(emp)}</div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile: stacked cards so the Delete/Review actions are always visible */}
                    <div className="md:hidden space-y-3">
                      {employees.map((emp) => (
                        <div key={emp.id} className="border border-gray-200 rounded-lg p-4" data-testid={`hr-pending-card-${emp.id}`}>
                          <div className="font-semibold text-gray-900">{emp.name}</div>
                          <div className="text-sm text-gray-600 break-all">{emp.email}</div>
                          <div className="mt-2">{statusBadge(emp)}</div>
                          <div className="mt-3 flex flex-wrap gap-2">{actionButtons(emp)}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-mx">
            <h3 className="text-lg font-semibold mb-4">Confirm Deletion</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{confirmDelete.name}</strong>? 
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteEmployee}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete Employee
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewingEmployee && reviewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-2xl font-semibold">Employee Onboarding Review</h3>
              <button
                onClick={() => {
                  setReviewingEmployee(null);
                  setReviewData(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Employee Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-lg mb-2">Employee Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <span className="ml-2 font-medium">{reviewData.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <span className="ml-2 font-medium">{reviewData.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <span className="ml-2">
                      {reviewData.status === 'pending_review' ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">
                          ✓ Complete - Ready for Review
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                          Step {Math.min(reviewData.current_step || 1, 8)}/8
                        </span>
                      )}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Completed:</span>
                    <span className="ml-2 font-medium">
                      {reviewData.completed_at ? new Date(reviewData.completed_at).toLocaleString(undefined, { timeZone: 'America/Toronto' }) : 'In Progress'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Onboarding Steps */}
              <div>
                <h4 className="font-semibold text-lg mb-3">Onboarding Progress</h4>
                <div className="space-y-2">
                  {[
                    { step: 1, name: 'Offer Letter Signed', key: 'offer_letter_signed' },
                    { step: 2, name: 'Application Info', key: 'application_info_completed' },
                    { step: 3, name: 'Certifications Check', key: 'cert_check_completed' },
                    { step: 4, name: 'Tax Forms', key: 'tax_forms_completed' },
                    { step: 5, name: 'Direct Deposit', key: 'direct_deposit_completed' },
                    { step: 6, name: 'Acknowledgement', key: 'acknowledgement_completed' },
                    { step: 7, name: 'Safety Video', key: 'video_watched' }
                  ].map(({ step, name, key }) => (
                    <div key={step} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        reviewData.onboarding_data?.[key] ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                      }`}>
                        {reviewData.onboarding_data?.[key] ? '✓' : step}
                      </span>
                      <span className="flex-1">{name}</span>
                      <span className="text-sm text-gray-500">
                        {reviewData.onboarding_data?.[key] ? 'Complete' : 'Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* HR Override — fix stuck onboardings (interrupted video, wrong step, etc.) */}
              <div className="border border-amber-300 bg-amber-50 rounded-lg p-4" data-testid="onboarding-override-panel">
                <h4 className="font-semibold text-lg mb-1 text-amber-900">HR Override</h4>
                <p className="text-xs text-amber-800 mb-3">
                  Fix a stuck onboarding: send them back to any step so they can resume, or mark the safety video watched.
                  Setting a step reopens the wizard (clears "pending" status).
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={overrideStep}
                    onChange={(e) => setOverrideStep(e.target.value)}
                    data-testid="override-step-select"
                    className="border border-amber-300 rounded px-2 py-1.5 text-sm bg-white"
                  >
                    <option value="">Jump to step…</option>
                    <option value="1">1 — Offer Letter</option>
                    <option value="2">2 — Application Info</option>
                    <option value="3">3 — Certifications Check</option>
                    <option value="4">4 — Direct Deposit</option>
                    <option value="5">5 — Tax Forms</option>
                    <option value="6">6 — Acknowledgement</option>
                    <option value="7">7 — Insurance Consent</option>
                    <option value="8">8 — Waivers & Policies</option>
                  </select>
                  <button
                    onClick={() => overrideStep && applyOverride({ current_step: parseInt(overrideStep, 10) })}
                    disabled={!overrideStep || overrideSaving}
                    data-testid="override-set-step-btn"
                    className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded text-sm font-medium"
                  >
                    {overrideSaving ? 'Saving…' : 'Set Step'}
                  </button>
                  {!reviewData.onboarding_data?.video_watched ? (
                    <button
                      onClick={() => window.confirm('Mark the safety video as watched for this employee?') && applyOverride({ video_watched: true })}
                      disabled={overrideSaving}
                      data-testid="override-video-watched-btn"
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-sm font-medium"
                    >
                      Mark Video Watched
                    </button>
                  ) : (
                    <span className="text-xs text-emerald-700 font-medium">✓ Safety video watched</span>
                  )}
                </div>
              </div>

              {/* Documents */}
              {reviewData.documents && reviewData.documents.length > 0 && (
                <div>
                  <h4 className="font-semibold text-lg mb-3">Submitted Documents</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {reviewData.documents.map((doc, idx) => (
                      <a
                        key={idx}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-2xl">📄</span>
                        <div className="flex-1">
                          <div className="font-medium">{doc.name}</div>
                          <div className="text-xs text-gray-500">{doc.type}</div>
                        </div>
                        <span className="text-blue-600">→</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {reviewData.status === 'pending_review' && (
                <div className="border-t pt-4 flex gap-3 justify-end">
                  <button
                    onClick={() => handleRejectEmployee(reviewingEmployee, reviewData.name)}
                    className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                  >
                    ✗ Reject
                  </button>
                  <button
                    onClick={() => {
                      handleApproveEmployee(reviewingEmployee, reviewData.name);
                      setReviewingEmployee(null);
                      setReviewData(null);
                    }}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    ✓ Approve Onboarding
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRAdminConsole;
